import fetchApi from '../util/fetchApi';
import { ApiEndpoint } from './ApiDefinition';
import checkErrors from './checkErrors';
import type { TenantCurrentImportDTO, TenantImportJobStartedDTO, TenantImportJobStatusDTO } from './TenantApi';

export type Dashboard = {
    id: number,
    // Environment-local EMBEDDED uuid (what the Superset embed SDK keys on). Not portable across
    // environments — do NOT store this; store `nativeUuid` and let the backend resolve embedded at embed time.
    uuid: string,
    // Portable NATIVE uuid — the stable identity we store in workpages and access grants.
    nativeUuid: string,
    name: string,
    thumbnailUrl: string,
}

export type DashboardEmbedToken = {
  url: string,
  token: string,
  // This environment's embedded uuid, resolved by the backend from the native uuid the token was
  // requested with. Pass this (not the stored native uuid) to the embed SDK's `embedDashboard({ id })`.
  embeddedUuid: string,
}

/**
 * One publicly published dashboard, as seen by an anonymous visitor. A deliberately thin projection of
 * `PublicDashboardAccess`: no publisher, no grant id, no acknowledgement trail — nothing an anonymous
 * caller has no business reading.
 */
export type PublicDashboardListing = {
  /** Portable NATIVE uuid; the path segment of the public page. */
  dashboardUuid: string,
  /** Display name, taken from the dashboard itself. */
  title: string | null,
  /** Reserved for a public-facing blurb; null until there is somewhere to author one. */
  description: string | null,
}

/**
 * Asset categories accepted by the reports import/export endpoints.
 *
 * Per-type imports (`Database`, `Dataset`, `Chart`, `Dashboard`, `SavedQuery`) honor the
 * `overwrite` flag and match by UUID.
 *
 * `All` uses Superset's bulk `assets/import` endpoint, which does **not** accept an `overwrite`
 * flag and unconditionally overwrites every asset matched by UUID — **including database
 * connection strings**. To preserve environment-specific DB connections, import databases
 * separately as `Database` with `overwrite: false`, or strip `databases/*.yaml` from the
 * archive before calling `importAssets({ type: 'All', ... })`.
 */
export type ReportsAssetType =
  | 'All'
  | 'Database'
  | 'Dataset'
  | 'Chart'
  | 'Dashboard'
  | 'SavedQuery';

const ReportsApi = {
  getReportsBackendUrl: ApiEndpoint<void, string>({
    url: 'manage/reports/backend-url',
  }),
  listDashboards: ApiEndpoint<void, Dashboard[]>({
    url: 'manage/reports/dashboards/list',
  }),
  getDashboardEmbedToken: ApiEndpoint<{dashboardUuid: string}, DashboardEmbedToken>({
    url: ({
      dashboardUuid
    }) => `manage/reports/dashboards/${dashboardUuid}/token`,
  }),

  // ── Anonymous (public dashboards) ────────────────────────────────────────────────────────────────
  // Unauthenticated twins of the two endpoints above, on the `anon/${tenant}/…` URL family already
  // used by the anonymous workflow routes (see `UWEEntityApi.getWorkflowInfoForSlugForAnonymous` and
  // `FormApi.getByNameForAnonymous`). `fetchApi.api` still sends `Authorization: Bearer null` on these
  // calls — harmless, and consistent with the existing anon endpoints, which ignore the header.
  //
  // BACKEND STATUS: NOT IMPLEMENTED — see docs/public-dashboards.md for the contract these two must
  // satisfy, in particular that the token endpoint serves ONLY dashboards with an active public grant
  // and derives every RLS constraint server-side.
  listPublicDashboardsForAnonymous: ApiEndpoint<{tenant: string | number}, PublicDashboardListing[]>({
    url: ({
      tenant
    }) => `anon/${tenant}/dashboards`,
  }),
  getPublicDashboardEmbedTokenForAnonymous: ApiEndpoint<{tenant: string | number, dashboardUuid: string}, DashboardEmbedToken>({
    url: ({
      tenant,
      dashboardUuid
    }) => `anon/${tenant}/dashboards/${dashboardUuid}/token`,
  }),

  /**
   * Downloads the raw Superset export archive (a ZIP of YAML files) as a `Blob`. Pair with
   * `triggerBlobDownload` to save it to disk, or hand the same `Blob` straight to
   * `importAssets` to round-trip it.
   */
  async exportAssets({ type, ids }: { type: ReportsAssetType, ids?: number[] }): Promise<Blob> {
    const params = new URLSearchParams({ type });
    if (ids && ids.length) params.set('ids', ids.join(','));
    const response: Response = await fetchApi.api.get(
      `manage/reports/assets/export?${params}`,
      { json: false },
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Export failed (HTTP ${response.status}).`);
    }
    return response.blob();
  },
  /**
   * Uploads a previously exported archive. `overwrite` defaults to `true` for per-type imports
   * and is **ignored** for `type: 'All'` (the bulk endpoint always overwrites matched assets,
   * including databases — see `ReportsAssetType` doc above). `passwords` (when provided) maps
   * a database YAML path (e.g. `databases/My_DB.yaml`) to its connection password.
   */
  async importAssets({
    type,
    file,
    overwrite,
    passwords,
  }: {
    type: ReportsAssetType,
    file: File | Blob,
    overwrite?: boolean,
    passwords?: Record<string, string>,
  }): Promise<boolean> {
    const formdata: Record<string, any> = { type, file };
    if (overwrite !== undefined) formdata.overwrite = String(overwrite);
    if (passwords) formdata.passwords = JSON.stringify(passwords);
    const response = await fetchApi.api.post('manage/reports/assets/import', { formdata });
    const checked: any = await checkErrors(response);
    return Boolean(checked?.data);
  },
  /**
   * Starts the same import as a background job; poll `getAssetsImportStatus`. Use this for "export all"
   * archives, which import one dashboard at a time and can outlast the request timeout. No `passwords`
   * (the job would store them); the export → import round trip doesn't need them.
   */
  async startAssetsImport({ type, file, overwrite }: { type: ReportsAssetType, file: File | Blob, overwrite?: boolean }): Promise<TenantImportJobStartedDTO> {
    const formdata: Record<string, any> = { type, file };
    if (overwrite !== undefined) formdata.overwrite = String(overwrite);
    return (await checkErrors(await fetchApi.api.post('manage/reports/assets/import/start', { formdata }))).data;
  },
  async getAssetsImportStatus({ jobId }: { jobId: number }): Promise<TenantImportJobStatusDTO> {
    return (await checkErrors(await fetchApi.api.get(`manage/reports/assets/import/${jobId}/status`))).data;
  },
  async getCurrentAssetsImport(): Promise<TenantCurrentImportDTO> {
    return (await checkErrors(await fetchApi.api.get('manage/reports/assets/import/current'))).data;
  },
};

export default ReportsApi;
